import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Dropdown, Table, ButtonGroup, Button } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import ExportButton from "./ExportButton";
import { exportToExcel } from "./exportExcel";

const HealthPlanProcedure = props => {
    const {
        className,
        innerRef,
        healthPlanSummary,
        ...attributes
    } = props;

    const [series, setSeries] = useState([]);
    const [seriesTotal, setSeriesTotal] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
    const [selectBtn, setSelectBtn] = useState("Geral");
    const [viewMode, setViewMode] = useState("table");

    useEffect(() => {
        changeSeries([...healthPlanSummary]);
    }, [healthPlanSummary]);

    function changeSeries(dataArray) {
        let newSeries = [];
        let totalSeries = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

        for (let indexSummary = 0; indexSummary < dataArray.length; indexSummary++) {
            const indexSeries = newSeries.findIndex(
                x => x.health_plan_id === dataArray[indexSummary].health_plan_id
            );

            if (indexSeries < 0) {
                newSeries.push({
                    health_plan_id: dataArray[indexSummary].health_plan_id,
                    health_plan_name: dataArray[indexSummary].health_plan_name,
                    quantity: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
                });
                newSeries[newSeries.length - 1].quantity[dataArray[indexSummary].month - 1] =
                    dataArray[indexSummary].count;
                newSeries[newSeries.length - 1].quantity[12] = dataArray[indexSummary].count;
            } else {
                newSeries[indexSeries].quantity[dataArray[indexSummary].month - 1] +=
                    dataArray[indexSummary].count;
                newSeries[indexSeries].quantity[12] += dataArray[indexSummary].count;
            }

            totalSeries[dataArray[indexSummary].month - 1] += dataArray[indexSummary].count;
            totalSeries[12] += dataArray[indexSummary].count;
        }

        const seriesSorted = newSeries.sort((a, b) =>
            String(a.health_plan_name).localeCompare(String(b.health_plan_name))
        );

        setSeries(seriesSorted);
        setSeriesTotal(totalSeries);
    }

    function handleChangeComplexity(id) {
        if (id !== "Geral") {
            if (id === "Complexa" || id === "Convencional") {
                const filter = id === "Complexa" ? "COM" : "NOR";
                const healthPlanSummaryFiltered = healthPlanSummary.filter(
                    x => x.complexity === filter
                );
                changeSeries([...healthPlanSummaryFiltered]);
            } else {
                const filter = id === "Apartamento" ? "APT" : "ENF";
                const healthPlanSummaryFiltered = healthPlanSummary.filter(
                    x => x.accommodation === filter
                );
                changeSeries([...healthPlanSummaryFiltered]);
            }
        } else {
            changeSeries([...healthPlanSummary]);
        }

        setSelectBtn(id);
    }

    const chartData = useMemo(() => {
        if (!series || series.length === 0) return { categories: [], data: [] };

        const sorted = [...series].sort((a, b) => b.quantity[12] - a.quantity[12]);
        const categories = sorted.map(s => s.health_plan_name);
        const data = sorted.map(s => s.quantity[12]);

        return { categories, data };
    }, [series]);

    const chartOptions = useMemo(() => {
        return {
            chart: {
                type: "bar",
                height: 350,
                toolbar: { show: false }
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: "55%",
                    endingShape: "rounded"
                }
            },
            dataLabels: { enabled: false },
            stroke: { show: true, width: 2, colors: ["transparent"] },
            xaxis: {
                categories: chartData.categories,
                labels: {
                    style: {
                        colors: "#000000",
                        fontSize: "12px",
                        fontFamily: "poppins",
                        fontWeight: 400
                    }
                }
            },
            yaxis: {
                title: { text: "Total de Procedimentos" },
                labels: {
                    style: {
                        colors: "#000000",
                        fontSize: "12px",
                        fontFamily: "poppins",
                        fontWeight: 400
                    }
                }
            },
            fill: { opacity: 1, colors: ["#1E90FF"] },
            tooltip: { y: { formatter: val => val } },
            title: {
                text: "Total de procedimentos por convênio",
                style: { fontFamily: "poppins", fontWeight: 600 }
            }
        };
    }, [chartData.categories]);

    const chartSeries = useMemo(
        () => [{ name: "Procedimentos", data: chartData.data }],
        [chartData.data]
    );

    function handleExport() {
        const months = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
        const toRow = (name, counter) => {
            const row = { "Convênios": name };
            months.forEach((month, i) => { row[month] = counter[i]; });
            row.TOTAL = counter[12];
            row["%"] = seriesTotal[12] > 0 ? Number(((counter[12] / seriesTotal[12]) * 100).toFixed(2)) : 0;
            return row;
        };

        const rows = series.map(seriesItem => toRow(seriesItem.health_plan_name, seriesItem.quantity));
        rows.push(toRow("TOTAL GERAL", seriesTotal));
        exportToExcel(rows, "Convenios", "procedimentos_por_convenio");
    }

    return (
        <>
            <div className="row">
                <div className="col-xl-10">
                    <div className="card-header border-0 flex-wrap pb-2">
                        <div className="chart-title mb-2 ">
                            <h2 className="heading">Procedimentos por Convênio</h2>
                        </div>
                    </div>
                </div>
                <div className="col-xl-2">
                    <div className="d-flex justify-content-end gap-2 align-items-center">
                        <Dropdown className="me-2 drop-select">
                            <Dropdown.Toggle
                                as="div"
                                className="i-false drop-select-btn style-dark"
                                style={{ backgroundColor: "#fff" }}
                            >
                                {selectBtn} <i className="fa-solid fa-angle-down"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item key="Geral" onClick={() => handleChangeComplexity("Geral")}>
                                    Geral
                                </Dropdown.Item>
                                <Dropdown.Item key="Complexa" onClick={() => handleChangeComplexity("Complexa")}>
                                    Complexa
                                </Dropdown.Item>
                                <Dropdown.Item key="Convencional" onClick={() => handleChangeComplexity("Convencional")}>
                                    Convencional
                                </Dropdown.Item>
                                <Dropdown.Item key="Apartamento" onClick={() => handleChangeComplexity("Apartamento")}>
                                    Apartamento
                                </Dropdown.Item>
                                <Dropdown.Item key="Enfermaria" onClick={() => handleChangeComplexity("Enfermaria")}>
                                    Enfermaria
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <ButtonGroup aria-label="View Mode">
                            <Button
                                variant={viewMode === "table" ? "primary" : "light"}
                                onClick={() => setViewMode("table")}
                            >
                                Tabela
                            </Button>
                            <Button
                                variant={viewMode === "chart" ? "primary" : "light"}
                                onClick={() => setViewMode("chart")}
                            >
                                Indicador
                            </Button>
                        </ButtonGroup>
                        <ExportButton onClick={handleExport} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    <div className="card-body pt-0 custome-tooltip">
                        {viewMode === "table" ? (
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>
                                            <strong>CONVÊNIOS</strong>
                                        </th>
                                        {["JAN","FEV","MAR","ABR","MAI","JUN","JUL","AGO","SET","OUT","NOV","DEZ"].map(m => (
                                            <th key={m} className="text-center">
                                                <strong>{m}</strong>
                                            </th>
                                        ))}
                                        <th className="text-center">
                                            <strong>TOTAL</strong>
                                        </th>
                                        <th className="text-center">
                                            <strong>%</strong>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {series.map(seriesItem => {
                                        const counter = seriesItem.quantity;
                                        let healthPlanPercent = 0;

                                        if (seriesTotal[12] > 0) {
                                            healthPlanPercent = ((counter[12] / seriesTotal[12]) * 100).toFixed(2);
                                        }

                                        return (
                                            <tr key={seriesItem.health_plan_id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <span className="w-space-no">{seriesItem.health_plan_name}</span>
                                                    </div>
                                                </td>
                                                {Array.from({ length: 12 }).map((_, i) => (
                                                    <td key={i}>
                                                        <div className="text-center">{counter[i]}</div>
                                                    </td>
                                                ))}
                                                <td>
                                                    <div className="text-center">
                                                        <strong>{counter[12]}</strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="text-end">{Number(healthPlanPercent).toLocaleString("pt-br", { minimumFractionDigits: 2 })} %</div>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    <tr key="TOTGER">
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <span className="w-space-no">
                                                    <strong>TOTAL GERAL</strong>
                                                </span>
                                            </div>
                                        </td>
                                        {Array.from({ length: 12 }).map((_, i) => (
                                            <td key={i}>
                                                <div className="text-center">
                                                    <strong>{seriesTotal[i]}</strong>
                                                </div>
                                            </td>
                                        ))}
                                        <td>
                                            <div className="text-center">
                                                <strong>{seriesTotal[12]}</strong>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="text-end">
                                                <strong>100,00 %</strong>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        ) : (
                            <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={380} />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

HealthPlanProcedure.propTypes = {
        children: PropTypes.node,
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
    innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    healthPlanSummary: PropTypes.array
};

HealthPlanProcedure.defaultProps = {
    healthPlanSummary: []
};

export default HealthPlanProcedure;